import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowUpRight,
  Download,
  Inbox,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/site";
import { whatsappLink } from "@/lib/site";
import { AdminChrome, type AdminTab } from "@/components/AdminChrome";
import { ContentManager } from "@/components/ContentManager";

type Row = {
  id: number;
  name: string;
  phone: string;
  preferredTime: string;
  enquiryType: string;
  message: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  country: string;
  createdAt: string;
  read: number;
};

type Stats = { total: number; unread: number; bridal: number; days: number };

type Filter = "all" | "unread" | string;

const EMPTY_STATS: Stats = { total: 0, unread: 0, bridal: 0, days: 0 };

function formatWhen(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function toCsv(rows: Row[]) {
  const headers = [
    "id",
    "created_at",
    "name",
    "phone",
    "enquiry_type",
    "preferred_time",
    "location",
    "latitude",
    "longitude",
    "country",
    "message",
    "is_read",
  ];
  const escape = (value: unknown) => {
    const text = value === null || value === undefined ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = rows.map(row =>
    [
      row.id,
      row.createdAt,
      row.name,
      row.phone,
      row.enquiryType,
      row.preferredTime,
      row.location,
      row.latitude,
      row.longitude,
      row.country,
      row.message,
      row.read ? "read" : "unread",
    ]
      .map(escape)
      .join(","),
  );
  return [headers.join(","), ...lines].join("\n");
}

/* ------------------------------------------------------------------ login */

function Login({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!response.ok || !data.ok) {
        setError(data.error ?? "Couldn't sign in.");
        return;
      }
      onSuccess();
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm border border-border bg-card/60 p-8 backdrop-blur-sm sm:p-10"
      >
        <span className="font-sans text-[0.625rem] tracking-[0.3em] text-gold uppercase">
          {SITE.tagline}
        </span>
        <h1 className="mt-3 font-display text-3xl text-foreground">Enquiries</h1>
        <p className="mt-2 font-sans text-xs leading-relaxed font-light text-muted-foreground">
          Sign in to read messages from the contact form.
        </p>

        <label
          htmlFor="admin-password"
          className="mt-8 block font-sans text-[0.625rem] tracking-[0.2em] text-muted-foreground uppercase"
        >
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoComplete="current-password"
          autoFocus
          className="mt-2.5 w-full border-b border-border bg-transparent pb-2.5 font-sans text-sm text-foreground outline-none transition-colors focus:border-gold"
        />

        {error && (
          <p className="mt-4 flex items-center gap-1.5 font-sans text-[0.7rem] text-destructive">
            <AlertCircle className="h-3 w-3 shrink-0" strokeWidth={1.5} />
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !password}
          className="mt-7 w-full cursor-pointer bg-foreground py-3.5 font-sans text-[0.625rem] tracking-[0.24em] text-background uppercase transition-colors duration-300 hover:bg-gold disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------- dashboard */

function StatCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="border border-border bg-card/50 px-5 py-4">
      <div className={cn("font-display text-3xl tabular-nums", tone ?? "text-foreground")}>
        {value}
      </div>
      <div className="mt-1 font-sans text-[0.6rem] tracking-[0.18em] text-muted-foreground uppercase">
        {label}
      </div>
    </div>
  );
}

function Dashboard({
  onSignOut,
  tab,
  onTab,
}: {
  onSignOut: () => void;
  tab: AdminTab;
  onTab: (tab: AdminTab) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [stats, setStats] = useState<Stats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listResponse, statsResponse] = await Promise.all([
        fetch("/api/admin/submissions", { cache: "no-store" }),
        fetch("/api/admin/submissions?stats=1", { cache: "no-store" }),
      ]);

      if (listResponse.status === 401) {
        onSignOut();
        return;
      }

      const list = (await listResponse.json().catch(() => ({}))) as {
        ok?: boolean;
        submissions?: Row[];
        error?: string;
      };
      if (!listResponse.ok || !list.ok) {
        setError(list.error ?? "Could not load enquiries.");
        return;
      }
      setRows(list.submissions ?? []);

      if (statsResponse.ok) {
        const statsData = (await statsResponse.json()) as { stats?: Stats };
        if (statsData.stats) setStats(statsData.stats);
      }
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setLoading(false);
    }
  }, [onSignOut]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleRead = async (id: number) => {
    // Capture the current value before the optimistic update, so the unread
    // counter moves in the right direction either way.
    const wasRead = rows.find(row => row.id === id)?.read === 1;
    setRows(prev =>
      prev.map(row => (row.id === id ? { ...row, read: wasRead ? 0 : 1 } : row)),
    );
    setStats(prev => ({
      ...prev,
      unread: Math.max(0, prev.unread + (wasRead ? 1 : -1)),
    }));
    try {
      const response = await fetch("/api/admin/submission", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (!response.ok) throw new Error("failed");
    } catch {
      setError("Couldn't update that enquiry.");
      void load();
    }
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this enquiry permanently?")) return;
    setRows(prev => prev.filter(row => row.id !== id));
    try {
      const response = await fetch("/api/admin/submission", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (!response.ok) throw new Error("failed");
      void load();
    } catch {
      setError("Couldn't delete that enquiry.");
      void load();
    }
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter(row => {
      if (filter === "unread" && row.read) return false;
      if (filter !== "all" && filter !== "unread" && row.enquiryType !== filter) return false;
      if (!needle) return true;
      return [row.name, row.phone, row.message, row.location, row.enquiryType]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, query, filter]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(visible)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const signOut = async () => {
    await fetch("/api/admin/logout", { method: "POST" }).catch(() => {});
    onSignOut();
  };

  const types = useMemo(
    () => [...new Set(rows.map(row => row.enquiryType).filter(Boolean))],
    [rows],
  );

  return (
    <div className="min-h-dvh bg-background">
      <AdminChrome
        title="Enquiries"
        tab={tab}
        onTab={onTab}
        onSignOut={() => void signOut()}
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              aria-label="Refresh"
              className="cursor-pointer border border-border p-2 text-muted-foreground transition-colors hover:border-gold hover:text-gold"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={visible.length === 0}
              className="inline-flex cursor-pointer items-center gap-2 border border-border px-3 py-2 font-sans text-[0.6rem] tracking-[0.16em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              <span className="hidden sm:inline">Export</span>
            </button>
          </>
        }
      />

      <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Unread" value={stats.unread} tone={stats.unread > 0 ? "text-gold" : undefined} />
          <StatCard label="Bridal" value={stats.bridal} />
          <StatCard label="Days active" value={stats.days} />
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60"
              strokeWidth={1.5}
            />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search name, phone, message…"
              aria-label="Search enquiries"
              className="w-full border border-border bg-transparent py-2.5 pr-3 pl-9 font-sans text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-gold"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {(["all", "unread", ...types] as Filter[]).map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setFilter(option)}
                className={cn(
                  "cursor-pointer border px-3 py-2 font-sans text-[0.6rem] tracking-[0.14em] uppercase transition-colors",
                  filter === option
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-border text-muted-foreground hover:border-gold/50 hover:text-gold",
                )}
              >
                {option === "all" ? "All" : option}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className="mt-5 flex items-center gap-1.5 border border-destructive/40 bg-destructive/5 px-4 py-3 font-sans text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
            {error}
          </p>
        )}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-24 font-sans text-xs tracking-[0.2em] text-muted-foreground uppercase">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            Loading
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Inbox className="h-8 w-8 text-muted-foreground/40" strokeWidth={1} />
            <p className="mt-4 font-display text-xl text-foreground">
              {rows.length === 0 ? "No enquiries yet" : "Nothing matches that filter"}
            </p>
            <p className="mt-1.5 max-w-xs font-sans text-xs leading-relaxed font-light text-muted-foreground">
              {rows.length === 0
                ? "Messages sent through the contact form will appear here."
                : "Try a different search term or filter."}
            </p>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-border border-y border-border">
            {visible.map(row => {
              const isOpen = expanded === row.id;
              const hasDetail =
                Boolean(row.message) || row.latitude !== null || Boolean(row.country);
              return (
                <li
                  key={row.id}
                  className={cn("transition-colors", !row.read && "bg-gold/[0.04]")}
                >
                  <div className="flex flex-wrap items-start gap-x-5 gap-y-3 px-1 py-4 sm:flex-nowrap sm:items-center">
                    <button
                      type="button"
                      onClick={() => void toggleRead(row.id)}
                      aria-label={row.read ? "Mark as unread" : "Mark as read"}
                      title={row.read ? "Mark as unread" : "Mark as read"}
                      className="mt-0.5 h-2.5 w-2.5 shrink-0 cursor-pointer rounded-full transition-colors sm:mt-0"
                    >
                      <span
                        className={cn(
                          "block h-full w-full rounded-full",
                          row.read ? "bg-transparent" : "bg-gold",
                        )}
                      />
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span
                          className={cn(
                            "truncate font-display text-lg",
                            row.read ? "text-foreground/80" : "text-foreground",
                          )}
                        >
                          {row.name}
                        </span>
                        {row.enquiryType && (
                          <span className="rounded-full border border-border px-2 py-0.5 font-sans text-[0.55rem] tracking-[0.14em] text-muted-foreground uppercase">
                            {row.enquiryType}
                          </span>
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-[0.7rem] font-light text-muted-foreground">
                        <a
                          href={whatsappLink(`Hi ${row.name}, about your enquiry on ${SITE.tagline}:`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 tabular-nums transition-colors hover:text-gold"
                        >
                          {row.phone}
                          <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                        </a>
                        {row.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" strokeWidth={1.5} />
                            {row.location}
                          </span>
                        )}
                        {row.preferredTime && <span>{row.preferredTime}</span>}
                        <span className="tabular-nums opacity-70">{formatWhen(row.createdAt)}</span>
                      </div>

                      {row.message && !isOpen && (
                        <p className="mt-2 truncate font-sans text-xs font-light text-muted-foreground/80">
                          {row.message}
                        </p>
                      )}
                    </div>

                    {hasDetail && (
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : row.id)}
                        aria-expanded={isOpen}
                        className="cursor-pointer border border-border px-2.5 py-1.5 font-sans text-[0.55rem] tracking-[0.14em] text-muted-foreground uppercase transition-colors hover:border-gold hover:text-gold"
                      >
                        {isOpen ? "Less" : "More"}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => void remove(row.id)}
                      aria-label="Delete enquiry"
                      className="cursor-pointer p-1.5 text-muted-foreground/50 transition-colors hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="grid gap-3 px-1 pb-5 pl-8 font-sans text-xs leading-relaxed font-light text-muted-foreground">
                      {row.message && (
                        <p className="border-l border-gold/40 pl-3 whitespace-pre-wrap text-foreground/80">
                          {row.message}
                        </p>
                      )}
                      <dl className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
                        <div className="flex gap-2">
                          <dt className="opacity-60">Received</dt>
                          <dd className="tabular-nums">{formatWhen(row.createdAt)}</dd>
                        </div>
                        {row.country && (
                          <div className="flex gap-2">
                            <dt className="opacity-60">Country</dt>
                            <dd>{row.country}</dd>
                          </div>
                        )}
                        {row.latitude !== null && row.longitude !== null && (
                          <div className="flex gap-2">
                            <dt className="opacity-60">Coordinates</dt>
                            <dd className="tabular-nums">
                              <a
                                href={`https://www.openstreetmap.org/?mlat=${row.latitude}&mlon=${row.longitude}#map=13/${row.latitude}/${row.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-gold hover:underline"
                              >
                                {row.latitude.toFixed(4)}, {row.longitude.toFixed(4)}
                                <ArrowUpRight className="h-3 w-3" strokeWidth={1.5} />
                              </a>
                            </dd>
                          </div>
                        )}
                        {row.preferredTime && (
                          <div className="flex gap-2">
                            <dt className="opacity-60">Prefers</dt>
                            <dd>{row.preferredTime}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <p className="mt-6 font-sans text-[0.65rem] text-muted-foreground/60">
          Showing {visible.length} of {rows.length}
        </p>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------- root */

export function AdminApp() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<AdminTab>("enquiries");

  // Probe the session on mount so a refresh keeps you signed in.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/admin/submissions?stats=1", { cache: "no-store" })
      .then(response => {
        if (!cancelled) setAuthed(response.ok);
      })
      .catch(() => {
        if (!cancelled) setAuthed(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authed === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return authed ? (
    tab === "enquiries" ? (
      <Dashboard onSignOut={() => setAuthed(false)} tab={tab} onTab={setTab} />
    ) : (
      <ContentManager tab={tab} onTab={setTab} onSignOut={() => setAuthed(false)} />
    )
  ) : (
    <Login onSuccess={() => setAuthed(true)} />
  );
}
